import { confirmSignIn, signIn, signOut } from 'aws-amplify/auth'
import {
    ConfirmConsumerV2OrderRequest,
    ConsumerOrderDetails,
    PrepareConsumerAuth200Response,
    PrepareConsumerAuthRequest
} from '../../model'
import { get, postPublic, put } from './api'

const handleOrderSignInWithEmail = async (email: string) => {
    return signIn({
        username: email,
        options: {
            authFlowType: 'CUSTOM_WITHOUT_SRP'
        }
    })
}

export const prepareOrderAuth = async (
    request: PrepareConsumerAuthRequest
): Promise<PrepareConsumerAuth200Response> => {
    const { data } = await postPublic('/v2/consumer/auth/prepare', {
        data: {
            ...request,
            domain: request.domain || window.location.hostname
        },
        headers: {
            'Content-Type': 'application/json'
        }
    })

    return data
}

export const startOrderSignIn = async (email: string) => {
    let result

    if (!email) {
        throw new Error('E-mailadres ontbreekt')
    }
    try {
        result = await handleOrderSignInWithEmail(email)
    } catch (error) {
        if (!isAlreadySignedInError(error)) {
            throw error
        }

        await signOut()
        result = await handleOrderSignInWithEmail(email)
    }

    if (!result.nextStep) {
        throw new Error('Sign-in response missing nextStep')
    }

    if (result.nextStep.signInStep !== 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        throw new Error(`Unexpected sign-in step: ${result.nextStep.signInStep}`)
    }

    return result
}

export const confirmOrderSignInVerificationCode = async (verificationCode: string) => {
    return confirmSignIn({
        challengeResponse: verificationCode
    })
}

export const retrieveOrder = async (): Promise<ConsumerOrderDetails> => {
    const { data } = await get('/v2/consumer/order')
    return data
}

export const confirmOrder = async (params: ConfirmConsumerV2OrderRequest): Promise<string> => {
    const { data } = await put('/v2/consumer/order/confirm', {
        data: params
    })
    return data
}

// Error handlings

const isAlreadySignedInError = (error: unknown) => {
    const name = (error as { name?: string })?.name
    return name === 'AlreadySignedInException' || name === 'UserAlreadyAuthenticatedException'
}

const getErrorMessages = (error: unknown) => {
    const messages: string[] = []
    const responseData = (error as { response?: { data?: unknown } })?.response?.data

    if (error instanceof Error && error.message) {
        messages.push(error.message.toLowerCase())
    }

    if (typeof responseData === 'string') {
        messages.push(responseData.toLowerCase())
    }

    if (responseData && typeof responseData === 'object') {
        const responseMessage = (responseData as { message?: string }).message
        const responseCode = (responseData as { code?: string }).code

        if (responseMessage) {
            messages.push(responseMessage.toLowerCase())
        }

        if (responseCode) {
            messages.push(responseCode.toLowerCase())
        }
    }

    return messages
}

export const resolvePrepareOrderAuthErrorMessage = (error: unknown) => {
    const errorMessages = getErrorMessages(error)

    const isInvalidOrderAccess = errorMessages.some(
        (message) =>
            message.includes('order not found for token') ||
            message.includes('record not found') ||
            message.includes('email does not match order')
    )
    if (isInvalidOrderAccess) {
        return 'Deze link of dit e-mailadres klopt niet. Controleer je e-mailadres en probeer opnieuw.'
    }

    return 'Er is een fout opgetreden bij het versturen van de verificatiecode. Probeer het opnieuw.'
}

export const resolveOrderVerificationErrorMessage = (error: unknown) => {
    const errorName = (error as { name?: string; __type?: string })?.name || (error as { __type?: string })?.__type

    if (errorName === 'CodeMismatchException' || errorName === 'NotAuthorizedException') {
        return 'De verificatiecode is onjuist. Controleer de code en probeer opnieuw.'
    }

    if (errorName === 'ExpiredCodeException') {
        return 'De verificatiecode is verlopen. Vraag een nieuwe code aan en probeer opnieuw.'
    }

    if (errorName === 'InvalidLambdaResponseException') {
        return 'De verificatie kon niet worden afgerond. Controleer je e-mailadres en vraag eventueel een nieuwe code aan.'
    }

    return 'Er is een fout opgetreden tijdens het controleren van de verificatiecode. Probeer het opnieuw.'
}
